import { Grid, MenuItem, FormControl } from '@mui/material';
import { FC, Dispatch, SetStateAction } from 'react';
import { useQuery } from 'react-query';
import {
  FiltersLayout,
  FiltersButtons,
  SearchField,
  DateRangePicker,
  SMBSelect
} from '../../../components';
import { IDateRange, IDropdownResponse } from '../../../models';
import { getWarrantyMessageJobStatuses } from '../../../fetch';

interface IWarrantyMessagesFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setHasAppliedFilters: Dispatch<SetStateAction<boolean>>;
  hasAppliedFilters: boolean;
  setSearchValue?: (val: string) => void;
  setSearchedValue?: (val: string) => void;
  searchValue?: string;
  handleSearch?: (val: string) => void;
  selectedDateRange?: IDateRange | null;
  setSelectedDateRange?: Dispatch<SetStateAction<IDateRange>>;
  searchPlaceholder?: string;
  handleClearSearch?: () => void;
  selectedJobStatus?: string;
  setSelectedJobStatus?: (val: string) => void;
}

export const WarrantyMessagesFilters: FC<IWarrantyMessagesFilters> = ({
  isLoading,
  applyFilters,
  setHasAppliedFilters,
  hasAppliedFilters,
  handleSearch,
  searchValue,
  setSearchValue,
  setSearchedValue,
  selectedDateRange,
  setSelectedDateRange,
  searchPlaceholder,
  handleClearSearch,
  selectedJobStatus,
  setSelectedJobStatus
}) => {
  const { isLoading: isLoadingJobStatuses, data: jobStatuses } = useQuery<
    IDropdownResponse[],
    Error
  >(['getWarrantyMessageJobStatuses'], () => getWarrantyMessageJobStatuses(), {
    notifyOnChangeProps: 'tracked'
  });

  return (
    <FiltersLayout>
      {handleSearch && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <SearchField
            isDisabled={isLoading}
            placeholder={searchPlaceholder}
            handleSearch={handleSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleClearSearch={handleClearSearch}
          />
        </Grid>
      )}

      {setSelectedJobStatus && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <SMBSelect
            label={isLoadingJobStatuses ? 'Loading...' : 'Job Status'}
            disabled={isLoading || isLoadingJobStatuses}
            value={selectedJobStatus || ''}
            onChange={e => {
              setSelectedJobStatus(e.target.value);
            }}
            handleClear={() => setSelectedJobStatus('')}
          >
            {jobStatuses &&
              !isLoadingJobStatuses &&
              jobStatuses.map((status, index) => {
                return (
                  <MenuItem key={`${status.value}-${index}`} value={status.value}>
                    {status.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
      )}

      {setSelectedDateRange && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="filterDateRange"
              label="Submission Date Range"
              value={selectedDateRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedDateRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} md={5} xl={3}>
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSelectedJobStatus && setSelectedJobStatus('');
            setSelectedDateRange &&
              setSelectedDateRange({
                startDate: null,
                endDate: null,
                key: 'selection',
                inputValue: ''
              });
            setSearchedValue && setSearchedValue('');
            setSearchValue && setSearchValue('');
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};
